import { useState, useEffect, useCallback } from "react";
import { supabase } from "../lib/supabaseClient";
import { insertLog } from "../services/logService";

// ── Shape raw DB row → app format ──
const shapeProject = (p) => ({
  id:                  p.id,
  title:               p.title              || "",
  clientName:          p.client_name        || "",
  totalContractValue:  parseFloat(p.total_contract_value) || 0,
  status:              p.status             || "Ongoing",
  startDate:           p.start_date         || "",
  deliveryDate:        p.delivery_date      || "",
  crmOpportunityId:    p.crm_opportunity_id || null,
  referrerName:        p.referrer_name      || "",
  referralPhone:       p.referral_phone     || "",
  referralRate:        parseFloat(p.referral_rate) || 0,
  assignedManagers:    (p.project_managers || []).map(m => {
    const e = m.employees;
    if (!e) return null;
    return { id: e.id, name: e.full_name || "", jobTitle: e.job_title || "", avatar: e.avatar_url || null };
  }).filter(Boolean),
  laborCost:     parseFloat(p.labor_cost_sum)    || 0,
  externalCosts: parseFloat(p.external_cost_sum) || 0,
  createdAt:     p.created_at || "",
});

export function useProjects() {
  const [projects, setProjects] = useState([]);
  const [loading,  setLoading]  = useState(true);

  const fetchAll = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from("projects")
        .select(`
          *,
          project_managers (
            employees:employee_id (id, full_name, job_title, avatar_url)
          ),
          labor_cost_sum:project_time_entries(hours, employee_id),
          external_cost_sum:project_expenses(amount)
        `)
        .order("created_at", { ascending: false });

      if (error) { console.warn("[Projects] fetch:", error.message); return; }

      const shaped = await Promise.all((data || []).map(async (p) => {
        let laborCost = 0;
        const timeEntries = p.labor_cost_sum || [];
        if (timeEntries.length > 0) {
          const empIds = [...new Set(timeEntries.map(t => t.employee_id).filter(Boolean))];
          if (empIds.length > 0) {
            const { data: emps } = await supabase.from("employees").select("id, hourly_rate").in("id", empIds);
            const rateMap = {};
            (emps || []).forEach(e => { rateMap[e.id] = parseFloat(e.hourly_rate) || 0; });
            laborCost = timeEntries.reduce((sum, t) => {
              const rate = rateMap[t.employee_id] || 0;
              return sum + (parseFloat(t.hours) || 0) * rate;
            }, 0);
          }
        }
        const externalCosts = (p.external_cost_sum || []).reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
        return {
          ...shapeProject(p),
          laborCost,
          externalCosts,
          forecastedProfit: (parseFloat(p.total_contract_value) || 0) - laborCost - externalCosts,
        };
      }));

      setProjects(shaped);
    } catch (err) {
      console.warn("[Projects] error:", err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const ch = supabase
      .channel("projects_realtime_v2")
      .on("postgres_changes", { event: "*", schema: "public", table: "projects" }, fetchAll)
      .on("postgres_changes", { event: "*", schema: "public", table: "project_time_entries" }, fetchAll)
      .on("postgres_changes", { event: "*", schema: "public", table: "project_expenses" }, fetchAll)
      .subscribe();
    return () => supabase.removeChannel(ch);
  }, [fetchAll]);

  // ── Create ──
  const createProject = useCallback(async (data) => {
    const { data: created, error } = await supabase
      .from("projects")
      .insert({
        title:               data.title || "Untitled Project",
        client_name:         data.clientName || "",
        total_contract_value: parseFloat(data.totalContractValue) || 0,
        status:              data.status || "Ongoing",
        start_date:          data.startDate || null,
        delivery_date:       data.deliveryDate || null,
        crm_opportunity_id:  data.crmOpportunityId || null,
        referrer_name:       data.referrerName || null,
        referral_phone:      data.referralPhone || null,
        referral_rate:       parseFloat(data.referralRate) || 0,
      })
      .select().single();

    if (error) throw new Error(error.message);

    if (data.assignedManagers?.length) {
      const unique = [...new Set(data.assignedManagers.map(m => m.id))];
      await supabase.from("project_managers").insert(unique.map(empId => ({ project_id: created.id, employee_id: empId })));
    }

    await supabase.from("project_activities").insert({
      project_id: created.id, action_type: "PROJECT_CREATED",
      description: `Project "${created.title}" was created`, created_at: new Date().toISOString(),
    }).catch(() => {});

    await insertLog({ action: "CREATE_PROJECT", actor_name: "System", details: `Created project: "${data.title}"`, timestamp: new Date().toISOString(), module: "Projects" }).catch(() => {});
    await fetchAll();
    return created;
  }, [fetchAll]);

  // ── Update ──
  const updateProject = useCallback(async (data) => {
    if (!data.id) return;
    const { error } = await supabase.from("projects").update({
      title:               data.title,
      client_name:         data.clientName,
      total_contract_value: parseFloat(data.totalContractValue) || 0,
      status:              data.status,
      start_date:          data.startDate || null,
      delivery_date:       data.deliveryDate || null,
      referrer_name:       data.referrerName || null,
      referral_phone:      data.referralPhone || null,
      referral_rate:       parseFloat(data.referralRate) || 0,
      updated_at:          new Date().toISOString(),
    }).eq("id", data.id);

    if (error) throw new Error(error.message);

    if (data.assignedManagers !== undefined) {
      await supabase.from("project_managers").delete().eq("project_id", data.id);
      if (data.assignedManagers.length) {
        const unique = [...new Set(data.assignedManagers.map(m => m.id))];
        await supabase.from("project_managers").insert(unique.map(empId => ({ project_id: data.id, employee_id: empId })));
      }
    }
    await fetchAll();
  }, [fetchAll]);

  // ── Delete ──
  const deleteProject = useCallback(async (id) => {
    const { error } = await supabase.from("projects").delete().eq("id", id);
    if (error) throw new Error(error.message);
    setProjects(prev => prev.filter(p => p.id !== id));
  }, []);

  return { projects, loading, fetchAll, createProject, updateProject, deleteProject };
}

// ───────────────────────────────────────────────
// Time Entries
// ───────────────────────────────────────────────
export const fetchTimeEntries = async (projectId) => {
  const { data, error } = await supabase
    .from("project_time_entries")
    .select(`
      *,
      employees:employee_id (
        id,
        full_name,
        hourly_rate,
        avatar_url
      )
    `)
    .eq("project_id", projectId)
    .order("created_at", { ascending: false });

  if (error) throw new Error(error.message);
  return data || [];
};

export async function fetchAllTimeEntries({ employeeId, projectId } = {}) {
  let q = supabase
    .from("project_time_entries")
    .select(`*, employees:employee_id (id, full_name, job_title, avatar_url, hourly_rate), projects:project_id (id, title)`)
    .order("week_start", { ascending: false });
  if (employeeId) q = q.eq("employee_id", employeeId);
  if (projectId)  q = q.eq("project_id",  projectId);
  const { data, error } = await q;
  if (error) throw new Error(error.message);
  return data || [];
}

export const logTimeEntry = async ({ projectId, employeeId, hours, weekStart, description }) => {
  try {
    const { data, error } = await supabase
      .from("project_time_entries")
      .insert([{
        project_id: projectId,
        employee_id: employeeId,
        hours: parseFloat(hours),
        week_start: weekStart,
        description: description || null,
        created_at: new Date().toISOString(),
      }])
      .select();

    if (error) throw new Error(error.message);
    return { data, error: null };
  } catch (error) {
    console.error("logTimeEntry error:", error);
    return { data: null, error: error.message };
  }
};

export async function deleteTimeEntry(id) {
  const { error } = await supabase.from("project_time_entries").delete().eq("id", id);
  if (error) throw new Error(error.message);
}

// ───────────────────────────────────────────────
// Expenses
// ───────────────────────────────────────────────
export async function fetchExpenses(projectId) {
  const { data, error } = await supabase.from("project_expenses").select("*").eq("project_id", projectId).order("created_at", { ascending: false });
  if (error) throw new Error(error.message);
  return data || [];
}

export async function addExpense({ projectId, description, amount }) {
  const { data, error } = await supabase.from("project_expenses").insert({ project_id: projectId, description, amount: parseFloat(amount), created_at: new Date().toISOString() }).select().single();
  if (error) throw new Error(error.message);
  return data;
}

export async function deleteExpense(id) {
  const { error } = await supabase.from("project_expenses").delete().eq("id", id);
  if (error) throw new Error(error.message);
}

// ───────────────────────────────────────────────
// Tasks (Trello board)
// ───────────────────────────────────────────────
export async function fetchTasks(projectId) {
  const { data, error } = await supabase
    .from("project_tasks")
    .select(`*, assigned_emp:assigned_to (id, full_name, avatar_url), created_emp:created_by (id, full_name)`)
    .eq("project_id", projectId)
    .order("position", { ascending: true });
  if (error) throw new Error(error.message);
  return data || [];
}

export async function createTask({ projectId, title, description, columnId, createdBy, assignedTo }) {
  const { data, error } = await supabase
    .from("project_tasks")
    .insert({ project_id: projectId, title, description: description || null, column_id: columnId || "todo", created_by: createdBy || null, assigned_to: assignedTo || null, created_at: new Date().toISOString(), updated_at: new Date().toISOString() })
    .select().single();
  if (error) throw new Error(error.message);
  return data;
}

export async function updateTaskColumn(taskId, columnId) {
  const { error } = await supabase.from("project_tasks").update({ column_id: columnId, updated_at: new Date().toISOString() }).eq("id", taskId);
  if (error) throw new Error(error.message);
}

export async function deleteTask(taskId) {
  const { error } = await supabase.from("project_tasks").delete().eq("id", taskId);
  if (error) throw new Error(error.message);
}

export async function fetchTaskUpdates(taskId) {
  const { data, error } = await supabase
    .from("task_updates")
    .select(`*, employees:employee_id (id, full_name, avatar_url)`)
    .eq("task_id", taskId)
    .order("created_at", { ascending: true });
  if (error) throw new Error(error.message);
  return data || [];
}

export async function addTaskUpdate({ taskId, employeeId, content }) {
  const { data, error } = await supabase
    .from("task_updates")
    .insert({ task_id: taskId, employee_id: employeeId || null, content, created_at: new Date().toISOString() })
    .select(`*, employees:employee_id (id, full_name, avatar_url)`)
    .single();
  if (error) throw new Error(error.message);
  return data;
}

// ───────────────────────────────────────────────
// Invoices
// ───────────────────────────────────────────────
export async function fetchInvoices(projectId) {
  const { data, error } = await supabase.from("project_invoices").select("*").eq("project_id", projectId).order("created_at", { ascending: true });
  if (error) throw new Error(error.message);
  return data || [];
}

export async function addInvoice({ projectId, invoiceNumber, issueDate, amount, notes }) {
  const { data, error } = await supabase
    .from("project_invoices")
    .insert({ project_id: projectId, invoice_number: invoiceNumber, issue_date: issueDate || null, amount: parseFloat(amount) || 0, notes: notes || null, is_issued: false, is_collected: false, created_at: new Date().toISOString() })
    .select().single();
  if (error) throw new Error(error.message);
  return data;
}

export async function updateInvoice(id, fields) {
  const { error } = await supabase.from("project_invoices").update({ ...fields, updated_at: new Date().toISOString() }).eq("id", id);
  if (error) throw new Error(error.message);
}

export async function deleteInvoice(id) {
  const { error } = await supabase.from("project_invoices").delete().eq("id", id);
  if (error) throw new Error(error.message);
}

// ───────────────────────────────────────────────
// Comments (kept for backward compat)
// ───────────────────────────────────────────────
export async function fetchComments(projectId) {
  const { data, error } = await supabase
    .from("project_comments")
    .select(`*, employees:employee_id (id, full_name, avatar_url, job_title)`)
    .eq("project_id", projectId)
    .order("created_at", { ascending: true });
  if (error) throw new Error(error.message);
  return data || [];
}

export async function postComment({ projectId, employeeId, content }) {
  const { data, error } = await supabase
    .from("project_comments")
    .insert({ project_id: projectId, employee_id: employeeId, content, created_at: new Date().toISOString() })
    .select(`*, employees:employee_id (id, full_name, avatar_url, job_title)`)
    .single();
  if (error) throw new Error(error.message);
  return data;
}

// ───────────────────────────────────────────────
// Activities
// ───────────────────────────────────────────────
export async function fetchActivities(projectId) {
  const { data, error } = await supabase
    .from("project_activities")
    .select(`*, employees:employee_id (id, full_name, avatar_url)`)
    .eq("project_id", projectId)
    .order("created_at", { ascending: false });
  if (error) throw new Error(error.message);
  return data || [];
}

export const logActivity = async ({ projectId, employeeId, actionType, description }) => {
  try {
    const { data, error } = await supabase
      .from("project_activities")
      .insert([{
        project_id: projectId,
        employee_id: employeeId || null,
        action_type: actionType,
        description: description,
        created_at: new Date().toISOString(),
      }])
      .select();

    if (error) throw new Error(error.message);
    return { data, error: null };
  } catch (error) {
    console.error("logActivity error:", error);
    return { data: null, error: error.message };
  }
};

export async function clearActivities(projectId) {
  const { error } = await supabase.from("project_activities").delete().eq("project_id", projectId);
  if (error) throw new Error(error.message);
}
